/* eslint-disable @typescript-eslint/no-explicit-any */
import ImageOptimize from '@/components/ImageOptimize'
import { useAuth } from '@/containers'
import useCurrencyNumber from '@/hooks/useCurrencyNumber'
import type {
  GiftItems,
  OrderService,
  OtherOffers,
  ProductDetail,
} from '@/types'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useMemo, useState } from 'react'
import { Autoplay, FreeMode, Grid } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import ProductAffiliateCashPromotion from '../ProductAffiliateCashPromotion'
import { FlashSaleProductQuantity } from '../ProductCard/components'
import ProductVoucherList from './ProductVoucherList'

const FlashSaleProductCountdown = dynamic(
  () => import('@/components/ProductCard/components/FlashSaleProductCountdown'),
  {
    ssr: false,
  }
)

interface ProductDetailPriceProps {
  entrySummaryData: ProductDetail
  listService?: OrderService[]
  listGift?: GiftItems[] | undefined
  listOffer?: OtherOffers[] | undefined
  pickUpInStore: boolean
  isOutOfStock: boolean
  bonusAmount?: number
}

const ProductDetailPrice = ({
  entrySummaryData,
  listService,
  listGift,
  listOffer,
  pickUpInStore,
  isOutOfStock,
  bonusAmount,
}: ProductDetailPriceProps) => {
  const [, setIsOpenVoucher] = useState(false)
  const format = useCurrencyNumber()
  const router = useRouter()
  const { user } = useAuth()

  useEffect(() => {
    const isApplyVoucher = router?.query?.voucher
    if (isApplyVoucher) {
      setIsOpenVoucher(true)
      localStorage.setItem('modalApplyVoucher', '1')
    }
  }, [router])

  const { price, finalPrice } = entrySummaryData
  const percent = ((price - finalPrice) / price) * 100

  const voucher = useMemo(
    () =>
      !!entrySummaryData.coupons?.length && (
        <>
          <ProductVoucherList
            data={entrySummaryData.coupons}
            listGift={listGift}
            listService={listService}
            pickUpInStore={pickUpInStore}
            listOffer={listOffer}
            addToCartData={entrySummaryData}
          />

          {/* <ModalVoucherPickUp
            isOpen={isOpenVoucher}
            setIsOpen={() => closeModalApplyVoucher()}
            data={entrySummaryData}
            listGift={listGift}
            listService={listService}
            isPreOrder={isPreOrder}
            pickUpInStore={pickUpInStore}
            listOffer={listOffer}
            addToCartData={entrySummaryData}
          /> */}
        </>
      ),
    [entrySummaryData.coupons]
  )

  const banner = useMemo(
    () =>
      !!entrySummaryData.banners?.length && (
        <div className='w-full min-w-0 overflow-hidden rounded-xl'>
          <Swiper
            modules={[Grid, FreeMode, Autoplay]}
            slidesPerView={1}
            loop={true}
            spaceBetween={8}
            autoplay={{ delay: 3500 }}
            speed={700}
            className='w-full'
          >
            {entrySummaryData.banners?.map((item, idx) => (
              <SwiperSlide key={'key' + idx}>
                <Link
                  href={item?.link || ''}
                  className='block w-full cursor-pointer overflow-hidden rounded-xl'
                >
                  <ImageOptimize
                    src={item?.image}
                    alt={item?.title}
                    className='h-auto w-full'
                  />
                </Link>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      ),
    [entrySummaryData.banners]
  )

  const isHasVoucherOrBanner = !!(voucher || banner)

  if (isOutOfStock) return null

  const lightProductAffiliateCashPromotion = (
    <ProductAffiliateCashPromotion
      className='mt-1 font-semibold'
      amount={bonusAmount}
      large
    />
  )

  const productAffiliateCashPromotion = (
    <ProductAffiliateCashPromotion
      className='mt-1 font-semibold'
      amount={bonusAmount}
      large
    />
  )

  if (entrySummaryData.flashSale && entrySummaryData.preOrder) {
    const { endTime, soldQuantity, quantity } = entrySummaryData.flashSale
    const { salePrice } = entrySummaryData.preOrder

    return (
      <div className='flex flex-col gap-3 rounded-xl bg-flashSalePriceBg px-3 pb-4 pt-1'>
        <div>
          <div className='flex'>
            <div className='grow pt-3'>
              <span className='block text-sm text-white'>
                Deal giá SHOCK{' '}
                <span className='font-bold'>
                  {user?.group && !user.group.default
                    ? `TV ${user.group.name}`
                    : ''}
                </span>
              </span>
              <span className='mt-2 block text-2xl font-bold text-white'>
                {format(finalPrice)}
              </span>
              <div className='flex items-center text-sm'>
                {!!percent && (
                  <span className='text-white line-through'>
                    {format(price, true)}
                  </span>
                )}
                {!!percent && (
                  <span className='ml-2 rounded bg-white px-1 pt-0.5 text-sm font-semibold text-secondary'>
                    -{percent?.toFixed(0)}%
                  </span>
                )}
              </div>
              {lightProductAffiliateCashPromotion}
            </div>
            <div className='flex grow flex-col items-center gap-0.5'>
              <div className='h-9 w-[1px] bg-white' />
              <span className='rounded bg-white p-1 text-xs'>Hoặc</span>
              <div className='h-9 w-[1px] bg-white' />
            </div>
            <div className='grow pt-3'>
              <span className='block text-sm text-white'>
                Đặt trước giá shock
              </span>
              <span className='mt-2 block text-2xl font-bold text-white'>
                {format(salePrice)}
              </span>
            </div>
          </div>
          <div className='mt-1 flex items-center gap-5'>
            <FlashSaleProductCountdown endTime={endTime} size='large' />
            <FlashSaleProductQuantity
              soldQuantity={soldQuantity}
              totalQuantity={quantity}
              className='w-[170px]'
            />
          </div>
        </div>
        {isHasVoucherOrBanner && (
          <div className='flex flex-col gap-3'>
            {voucher}
            {banner}
          </div>
        )}
      </div>
    )
  }

  if (entrySummaryData.exclusiveDeal && entrySummaryData.preOrder) {
    const { salePrice } = entrySummaryData.preOrder

    return (
      <div className='flex flex-col gap-3 rounded-xl bg-flashSalePriceBg px-3 pb-4 pt-1'>
        <div>
          <div className='flex'>
            <div className='grow pt-3'>
              <span className='block text-sm text-white'>
                Deal giá SHOCK{' '}
                <span className='font-bold'>
                  {user?.group && !user.group.default
                    ? `TV ${user.group.name}`
                    : ''}
                </span>
              </span>
              <span className='mt-2 block text-2xl font-bold text-white'>
                {format(finalPrice)}
              </span>
              <div className='flex items-center text-sm'>
                {!!percent && (
                  <span className='text-white line-through'>
                    {format(price, true)}
                  </span>
                )}
                {!!percent && (
                  <span className='ml-2 rounded bg-white px-1 pt-0.5 text-sm font-semibold text-secondary'>
                    -{percent?.toFixed(0)}%
                  </span>
                )}
              </div>
              {lightProductAffiliateCashPromotion}
            </div>
            <div className='flex grow flex-col items-center gap-0.5'>
              <div className='h-9 w-[1px] bg-white' />
              <span className='rounded bg-white p-1 text-xs'>Hoặc</span>
              <div className='h-9 w-[1px] bg-white' />
            </div>
            <div className='grow pt-3'>
              <span className='block text-sm text-white'>
                Đặt trước giá shock
              </span>
              <span className='mt-2 block text-2xl font-bold text-white'>
                {format(salePrice)}
              </span>
            </div>
          </div>
        </div>
        {isHasVoucherOrBanner && (
          <div className='flex flex-col gap-3'>
            {voucher}
            {banner}
          </div>
        )}
      </div>
    )
  }

  if (entrySummaryData.preOrder) {
    const { salePrice } = entrySummaryData.preOrder

    return (
      <div className='flex flex-col gap-3 rounded-xl border border-[#FDE68A] bg-productDetailPriceBg px-3 pb-4 pt-1'>
        <div className='flex'>
          <div className='grow pt-3'>
            <span className='block text-sm text-main'>
              Mua ngay với giá{' '}
              <span className='font-bold'>
                {user?.group && !user.group.default
                  ? `TV ${user.group.name}`
                  : ''}
              </span>
            </span>
            <span className='mt-2 block text-2xl font-bold text-secondary'>
              {format(finalPrice)}
            </span>
            <div className='flex items-center text-sm'>
              {!!percent && (
                <span className='text-sub line-through'>
                  {format(price, true)}
                </span>
              )}
              {!!percent && (
                <span className='ml-2 rounded bg-secondary-20 px-1 pt-0.5 text-sm font-semibold text-secondary'>
                  -{percent?.toFixed(0)}%
                </span>
              )}
            </div>
            {productAffiliateCashPromotion}
          </div>
          <div className='flex grow flex-col items-center gap-0.5'>
            <div className='h-9 w-[1px] bg-[#F3F4F6]' />
            <span className='rounded bg-white p-1 text-xs'>Hoặc</span>
            <div className='h-9 w-[1px] bg-[#F3F4F6]' />
          </div>
          <div className='grow pt-3'>
            <span className='block text-sm text-main'>Đặt trước giá shock</span>
            <span className='mt-2 block text-2xl font-bold text-secondary'>
              {format(salePrice)}
            </span>
          </div>
        </div>

        {isHasVoucherOrBanner && (
          <div className='flex flex-col gap-3'>
            {voucher}
            {banner}
          </div>
        )}
      </div>
    )
  }

  if (entrySummaryData.exclusiveDeal) {
    return (
      <div className='flex flex-col gap-3 rounded-xl bg-flashSalePriceBg px-3 pb-4 pt-1'>
        <div className='flex items-center justify-between'>
          <div className='grow pt-3'>
            <span className='block text-sm text-white'>Deal độc quyền</span>
            <span className='mt-2 block text-2xl font-bold text-white'>
              {format(finalPrice)}
            </span>
            <div className='flex items-center text-sm'>
              {!!percent && (
                <span className='text-white line-through'>
                  {format(price, true)}
                </span>
              )}
              {!!percent && (
                <span className='ml-2 rounded bg-white px-1 pt-0.5 text-sm font-semibold text-secondary'>
                  -{percent?.toFixed(0)}%
                </span>
              )}
            </div>
            {lightProductAffiliateCashPromotion}
          </div>
        </div>

        {isHasVoucherOrBanner && (
          <div className='flex flex-col gap-3'>
            {voucher}
            {banner}
          </div>
        )}
      </div>
    )
  }

  if (entrySummaryData.flashSale) {
    const { endTime, soldQuantity, quantity } = entrySummaryData.flashSale

    return (
      <div className='flex flex-col gap-3 rounded-xl bg-flashSalePriceBg px-3 pb-4 pt-1'>
        <div className='flex items-center justify-between'>
          <div className='grow pt-3'>
            <span className='block text-sm text-white'>
              Deal giá SHOCK{' '}
              <span className='font-bold'>
                {user?.group && !user.group.default
                  ? `TV ${user.group.name}`
                  : ''}
              </span>
            </span>
            <span className='mt-2 block text-2xl font-bold text-white'>
              {format(finalPrice)}
            </span>
            <div className='flex items-center text-sm'>
              {!!percent && (
                <span className='text-white line-through'>
                  {format(price, true)}
                </span>
              )}
              {!!percent && (
                <span className='ml-2 rounded bg-white px-1 pt-0.5 text-sm font-semibold text-secondary'>
                  -{percent?.toFixed(0)}%
                </span>
              )}
            </div>
            {lightProductAffiliateCashPromotion}
          </div>
          <div className='flex flex-col items-center gap-2'>
            <FlashSaleProductCountdown endTime={endTime} size='large' />
            <FlashSaleProductQuantity
              soldQuantity={soldQuantity}
              totalQuantity={quantity}
              className='w-[170px]'
            />
          </div>
        </div>

        {isHasVoucherOrBanner && (
          <div className='flex flex-col gap-3'>
            {voucher}
            {banner}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className='flex flex-col gap-3 rounded-xl border border-[#FDE68A] bg-productDetailPriceBg px-3 pb-4 pt-4'>
      <div className='flex flex-col items-start'>
        <span className='block text-sm text-main'>
          Mua ngay với giá{' '}
          <span className='font-bold'>
            {user?.group && !user.group.default ? `TV ${user.group.name}` : ''}
          </span>
        </span>
        <span className='mt-2 block text-2xl font-bold text-secondary'>
          {format(finalPrice)}
        </span>
        <div className='flex items-center text-sm'>
          {!!percent && (
            <span className='text-sub line-through'>{format(price, true)}</span>
          )}
          {!!percent && (
            <span className='ml-2 rounded bg-secondary-20 px-1 pt-0.5 text-sm font-semibold text-secondary'>
              -{percent?.toFixed(0)}%
            </span>
          )}
        </div>
        {productAffiliateCashPromotion}
      </div>

      {isHasVoucherOrBanner && (
        <div className='flex flex-col gap-3'>
          {voucher}
          {banner}
        </div>
      )}
    </div>
  )
}
export default ProductDetailPrice
